import { useQuery } from '@tanstack/react-query';
import { get, post } from '@/lib/api/client';

/**
 * Settlements — each terminal batch as it closed, beside the POS's own record of
 * that batch.
 *
 * Built on `analytics/v2/reports/settlements` rather than the legacy
 * `pos/terminalsettlement/report`, which any signed-in account could read for
 * every store, shipped each batch's receipt HTML inline, and filled the refund
 * counts of three categories from the debit column. See
 * `docs/api-findings-sales.md`.
 *
 * The POS side is matched by the batch number the terminal stamps on every
 * transaction. Measured over 260 production batches before it was written: 244
 * agree to the cent, every refund total agrees, and in the rest the terminal is
 * always the higher.
 */

/** One of the terminal's three categories. `SettlementTenderDto`. */
export interface SettlementTender {
  saleCount: number;
  saleAmount: number;
  refundCount: number;
  refundAmount: number;
}

/** `SettlementBatchDto`. Timestamps are UTC without a zone marker. */
export interface SettlementBatch {
  id: string;
  settledAt: string;
  /** The lane's previous settlement. Null when it has none. */
  openedAt: string | null;
  businessId: string | null;
  businessName: string | null;
  laneId: string | null;
  laneTag: string | null;
  isSelfCheckout: boolean;
  /** What the terminal answered, e.g. "BALANCED 518 BATCH COMPLETE". */
  status: string | null;
  hasReceipt: boolean;
  /** Visa, Mastercard, American Express, Discover. */
  credit: SettlementTender;
  /** ATH debit cards and ATH Móvil. */
  debit: SettlementTender;
  /** The terminal's "Cash" category — the POS records it as "CASH CARD". */
  cash: SettlementTender;
  saleCount: number;
  sales: number;
  refundCount: number;
  refunds: number;
  net: number;
  /** False when the POS side could not be gathered; every `pos*` is then 0. */
  compared: boolean;
  terminalId: string | null;
  batchNumber: string | null;
  posSaleCount: number;
  posSales: number;
  posRefundCount: number;
  posRefunds: number;
  /** `sales − posSales`. Positive: the terminal took money the POS never saved. */
  salesDifference: number;
  refundsDifference: number;
}

/** `SettlementsTotalsDto`. */
export interface SettlementTotals {
  batches: number;
  credit: SettlementTender;
  debit: SettlementTender;
  cash: SettlementTender;
  saleCount: number;
  sales: number;
  refundCount: number;
  refunds: number;
  net: number;
  comparedBatches: number;
  batchesWithDifference: number;
  /** Terminal sales over the compared batches only. */
  comparedSales: number;
  posSales: number;
  salesDifference: number;
  comparedRefunds: number;
  posRefunds: number;
  refundsDifference: number;
}

export interface SettlementsReport {
  totals: SettlementTotals;
  batches: SettlementBatch[];
}

export interface SettlementsQuery {
  /** `yyyy-MM-dd`, a day at the store. */
  start: string;
  /** `yyyy-MM-dd`, included. */
  end: string;
  /** Encoded id, or null for every store the account can see. */
  businessId: string | null;
  laneId: string | null;
}

/**
 * Both picked days go out raw and both are included — the endpoint adds the
 * day itself, like the v1 reports and unlike `trends/*`.
 */
export function useSettlements(query: SettlementsQuery) {
  return useQuery({
    queryKey: ['settlements', query],
    staleTime: 60_000,
    queryFn: () =>
      post<SettlementsReport>('analytics/v2/reports/settlements', {
        startDate: query.start,
        endDate: query.end,
        businessIds: query.businessId ? [query.businessId] : null,
        posLaneId: query.laneId,
      }),
    select: (report) => ({ ...report, batches: report.batches ?? [] }),
  });
}

/** The receipt HTML the terminal printed, fetched only when someone opens it. */
export function useSettlementReceipt(settlementId: string | null) {
  return useQuery({
    queryKey: ['settlements', 'receipt', settlementId],
    enabled: Boolean(settlementId),
    staleTime: Infinity,
    queryFn: () =>
      get<string | null>(`analytics/v2/reports/settlements/${settlementId}/receipt`),
  });
}

const RECEIPT_STYLESHEET =
  'https://grocefystorage.blob.core.windows.net/evertecterminal/ReceiptStyle.css';
const ATH_LOGO =
  'https://grocefystorage.blob.core.windows.net/evertecterminal/ath-logo-02.png';

/**
 * The terminal's receipt made renderable on its own.
 *
 * It arrives expecting the terminal's local stylesheet and logo; both are
 * hosted, and the legacy page swapped them in the same way. Empty label and
 * content cells get a non-breaking space so the receipt's rows keep their
 * height.
 */
export function prepareReceipt(html: string): string {
  const stylesheet = `<link rel="stylesheet" type="text/css" href="${RECEIPT_STYLESHEET}">`;
  const styled = html.includes('</title>')
    ? html.replace('</title>', `</title>${stylesheet}`)
    : `${stylesheet}${html}`;
  return styled
    .replace(/ATHLogo\.png/g, ATH_LOGO)
    .replace(/<div class="CTLS_(CONTENT|LABEL)">\s*<\/div>/g, '<div class="CTLS_$1">&nbsp;</div>');
}
